/* eslint-disable no-bitwise */
import { inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PlatformService } from '@rma/public/generic/util/environment';
import { animationFrameScheduler, concat, filter, interval, map, of, Subscription, takeWhile, timer, withLatestFrom } from 'rxjs';

declare global {
  interface Window {
    webkitRequestAnimationFrame?: () => void;
    mozRequestAnimationFrame?: () => void;
    oRequestAnimationFrame?: () => void;
    msRequestAnimationFrame?: () => void;
  }
}

interface Particle {
  color: string;
  color2: string;
  x: number;
  y: number;
  diameter: number;
  tilt: number;
  tiltAngleIncrement: number;
  tiltAngle: number;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class ConfettiService {
  private readonly platformService = inject(PlatformService);

  private readonly colors = ['#c204e9', '#00837b', '#30d4c8', '#e51a41', '#fbb116', '#0075e4', '#128718', '#6c00c1'];

  private readonly gradient = false;
  private readonly maxCount = 150;
  private readonly speed = 2;
  private waveAngle = 0;

  private canvas: HTMLCanvasElement | null = null;
  private pause = false;
  private particles: Particle[] = [];

  private scheduler$?: Subscription;

  private get context(): CanvasRenderingContext2D | null {
    return this.canvas?.getContext('2d') ?? null;
  }

  public start(durationMilliseconds?: number, min?: number, max?: number): void {
    if (this.platformService.isPlatformServer) {
      return;
    }

    const width = window.innerWidth;
    const height = window.innerHeight;

    // SAFETY(legacy): unreviewed, pre-dates this rule.
    this.canvas = document.getElementById('confetti-canvas') as HTMLCanvasElement | null;

    if (this.canvas === null) {
      this.canvas = document.createElement('canvas');
      this.canvas.setAttribute('id', 'confetti-canvas');
      this.canvas.setAttribute('style', 'display:block;z-index:999999;pointer-events:none;position:fixed;top:0');
      document.body.prepend(this.canvas);
      this.canvas.width = width;
      this.canvas.height = height;
      window.addEventListener(
        'resize',
        () => {
          if (this.canvas) {
            this.canvas.width = window.innerWidth;
            this.canvas.height = window.innerHeight;
          }
        },
        true,
      );
    }

    let count = this.maxCount;
    if (min) {
      if (max) {
        if (min === max) {
          count = this.particles.length + max;
        } else {
          if (min > max) {
            const temp = min;
            min = max;
            max = temp;
          }
          count = this.particles.length + ((Math.random() * (max - min) + min) | 0);
        }
      } else {
        count = this.particles.length + min;
      }
    } else if (max) {
      count = this.particles.length + max;
    }

    while (this.particles.length < count) {
      this.particles.push(this.resetParticle(width, height));
    }

    this.pause = false;

    const fadeAway$ = durationMilliseconds ? concat(of(false), timer(durationMilliseconds).pipe(map(() => true))) : of(false);

    this.scheduler$ = interval(0, animationFrameScheduler)
      .pipe(
        filter((_) => !this.pause),
        takeWhile((_) => !!this.particles.length),
        withLatestFrom(fadeAway$),
        untilDestroyed(this),
      )
      .subscribe(([_, x]) => this.runAnimation(x));
  }

  public stop(): void {
    this.pause = false;
    this.scheduler$?.unsubscribe();
    this.scheduler$ = undefined;
    this.context?.clearRect(0, 0, window.innerWidth, window.innerHeight);
  }

  public togglePause(): void {
    if (this.pause) {
      this.resumeConfetti();
    } else {
      this.pauseConfetti();
    }
  }

  public isPaused(): boolean {
    return this.pause;
  }

  public toggle(): void {
    if (this.scheduler$) {
      this.stop();
    } else {
      this.start();
    }
  }

  public isRunning(): boolean {
    return !!(this.scheduler$ && !this.scheduler$.closed);
  }

  private resetParticle(width: number, height: number, particle?: Particle): Particle {
    return {
      ...particle,
      color: this.colors[(Math.random() * this.colors.length) | 0],
      color2: this.colors[(Math.random() * this.colors.length) | 0],
      x: Math.random() * width,
      y: Math.random() * height - height,
      diameter: Math.random() * 10 + 5,
      tilt: Math.random() * 10 - 10,
      tiltAngleIncrement: Math.random() * 0.07 + 0.05,
      tiltAngle: Math.random() * Math.PI,
    };
  }

  private pauseConfetti(): void {
    this.pause = true;
  }

  private resumeConfetti(): void {
    this.pause = false;
  }

  private runAnimation(fadeAway: boolean): void {
    if (!this.context) {
      return;
    }

    if (this.particles.length === 0) {
      this.context?.clearRect(0, 0, window.innerWidth, window.innerHeight);
    } else {
      this.context?.clearRect(0, 0, window.innerWidth, window.innerHeight);
      this.updateParticles(fadeAway);
      this.drawParticles(this.context);
    }
  }

  private drawParticles(context: CanvasRenderingContext2D): void {
    let particle;
    let x;
    let x2;
    let y2;
    for (let i = 0; i < this.particles.length; i++) {
      particle = this.particles[i];
      context.beginPath();
      context.lineWidth = particle.diameter;
      x2 = particle.x + particle.tilt;
      x = x2 + particle.diameter / 2;
      y2 = particle.y + particle.tilt + particle.diameter / 2;
      if (this.gradient) {
        const gradient = context.createLinearGradient(x, particle.y, x2, y2);
        gradient.addColorStop(0, particle.color);
        gradient.addColorStop(1, particle.color2);
        context.strokeStyle = gradient;
      } else {
        context.strokeStyle = particle.color;
      }
      context.moveTo(x, particle.y);
      context.lineTo(x2, y2);
      context.stroke();
    }
  }

  private updateParticles(fadeAway: boolean): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    let particle;
    this.waveAngle += 0.01;
    for (let i = 0; i < this.particles.length; i++) {
      particle = this.particles[i];

      if (fadeAway && particle.y < -15) {
        particle.y = height + 100;
      } else {
        particle.tiltAngle += particle.tiltAngleIncrement;
        particle.x += Math.sin(this.waveAngle) - 0.5;
        particle.y += (Math.cos(this.waveAngle) + particle.diameter + this.speed) * 0.5;
        particle.tilt = Math.sin(particle.tiltAngle) * 15;
      }
      if (particle.x > width + 20 || particle.x < -20 || particle.y > height) {
        if (!fadeAway && this.particles.length <= this.maxCount) {
          this.particles[i] = this.resetParticle(width, height, particle);
        } else {
          this.particles.splice(i, 1);
          i--;
        }
      }
    }
  }
}
